import { useQuery } from "@tanstack/react-query";
import { useClientId } from "@/shared/hooks/useClientId";
import { supabase } from "@/shared/integrations/supabase/client";
import {
  parseJson,
  type DeficiencyPayload,
  type RawDeficiency,
  type RawRecommendation,
} from "@/features/properties/components/propertyOverview/deficiencyHelpers";
import { getAuditSite } from "./api";
import { normalizeAuditSite } from "./normalizeAudit";

export type AuditSource = "audit" | "legacy";

/** 404 (not computed yet) or 403 (no access) → fall back to the legacy blob. */
export function isNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : "";
  return /^(404|403)\b/.test(msg);
}

/** Read the legacy property_deficiencies_and_recommendations blob. */
export async function loadLegacyDeficiencies(
  propertyId: string,
): Promise<{ deficiencies: RawDeficiency[]; recommendations: RawRecommendation[] }> {
  const { data: row, error } = await supabase
    .from("property_deficiencies_and_recommendations")
    .select("data")
    .eq("property_id", propertyId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const payload = parseJson<DeficiencyPayload>(row?.data, {});
  return {
    deficiencies: Array.isArray(payload.deficiencies) ? payload.deficiencies : [],
    recommendations: Array.isArray(payload.recommendations) ? payload.recommendations : [],
  };
}

interface AuditResult {
  deficiencies: RawDeficiency[];
  recommendations: RawRecommendation[];
  score: number | null;
  recommendationTotal: number | null;
  narrativeHeadline: string | null;
  source: AuditSource;
}

const legacyResult = (legacy: {
  deficiencies: RawDeficiency[];
  recommendations: RawRecommendation[];
}): AuditResult => ({
  ...legacy,
  score: null,
  recommendationTotal: null,
  narrativeHeadline: null,
  source: "legacy",
});

/**
 * Property deficiencies from the live audit engine, falling back to the legacy
 * blob when the property has no audit findings yet (404 / 403 / empty) — not all
 * properties are on the new schema during rollout. Returns a `source` flag so
 * the UI shows engine-only affordances (trace, recommendations, feedback) for
 * audit-sourced findings only.
 */
export function useAuditDeficiencies(propertyId: string) {
  const { clientId } = useClientId();

  const query = useQuery<AuditResult>({
    queryKey: ["audit-deficiencies", clientId, propertyId],
    enabled: !!propertyId && !!clientId,
    queryFn: async (): Promise<AuditResult> => {
      try {
        const n = normalizeAuditSite(await getAuditSite(clientId as string, propertyId));
        if (n.deficiencies.length === 0) {
          return legacyResult(await loadLegacyDeficiencies(propertyId));
        }
        return {
          deficiencies: n.deficiencies,
          recommendations: n.recommendations,
          score: n.score,
          recommendationTotal: n.recommendationTotal,
          narrativeHeadline: n.narrativeHeadline,
          source: "audit",
        };
      } catch (err) {
        if (isNotFound(err)) {
          return legacyResult(await loadLegacyDeficiencies(propertyId));
        }
        throw err;
      }
    },
  });

  return {
    deficiencies: query.data?.deficiencies ?? [],
    recommendations: query.data?.recommendations ?? [],
    score: query.data?.score ?? null,
    recommendationTotal: query.data?.recommendationTotal ?? null,
    narrativeHeadline: query.data?.narrativeHeadline ?? null,
    source: query.data?.source ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
